import { useEffect, useRef } from 'react';

/**
 * Custom hook to track screen sharing status in the browser and sync it with our app state
 * 
 * This hook provides a way to detect when screen sharing has been stopped directly from
 * the browser UI (without using our app's controls) and update our app state accordingly.
 * 
 * @param isScreenSharing Current screen sharing state in our application
 * @param videoStream Current video stream reference
 * @param stopScreenSharingCallback Callback to execute when screen sharing stops
 */
const useScreenShareTracker = (
  isScreenSharing: boolean,
  videoStream: MediaStream | null,
  stopScreenSharingCallback: () => void
) => {
  // Ref to track the current screen sharing tracks
  const screenTrackRef = useRef<MediaStreamTrack | null>(null);
  
  // Ref to track if a state mismatch correction is in progress
  const correctionInProgressRef = useRef(false);
  
  // Effect to set up track ended listeners and sync state
  useEffect(() => {
    // Skip if correction is already in progress
    if (correctionInProgressRef.current) {
      console.log('[useScreenShareTracker] Skipping check due to active correction');
      return;
    }
    
    const findScreenTrack = (stream: MediaStream | null): MediaStreamTrack | null => {
      if (!stream) return null;
      
      // Get all video tracks
      const videoTracks = stream.getVideoTracks();
      
      if (videoTracks.length === 0) {
        console.log('[useScreenShareTracker] No video tracks found in stream');
        return null;
      }
      
      console.log('[useScreenShareTracker] Analyzing video tracks:', 
        videoTracks.map(t => `${t.label} (${t.readyState})`));
      
      // First, look for definite screen sharing tracks based on label
      for (const track of videoTracks) {
        const label = track.label.toLowerCase();
        
        // Check if this is explicitly a screen track by name
        if (label.includes('screen') || 
            label.includes('display') || 
            label.includes('window') || 
            label.includes('tab') || 
            label.includes('monitor') ||
            label.includes('sharing')) {
          console.log(`[useScreenShareTracker] Found definite screen track: ${label}`);
          return track;
        }
      }
      
      // Look at settings for clues
      for (const track of videoTracks) {
        if (track.getSettings) {
          const settings = track.getSettings();
          
          // Screen sharing typically has displaySurface property or unusual aspect ratios
          if (settings.displaySurface) {
            console.log(`[useScreenShareTracker] Found screen track via displaySurface: ${settings.displaySurface}`);
            return track;
          }
          
          // Screen sharing often has larger dimensions than cameras
          if (settings.width && settings.height) {
            // Check for unusually large resolution or desktop-like aspect ratios
            if (settings.width >= 1920 || settings.height >= 1080 ||
                Math.abs((settings.width / settings.height) - (16 / 9)) < 0.1 ||
                Math.abs((settings.width / settings.height) - (16 / 10)) < 0.1) {
              console.log(`[useScreenShareTracker] Likely screen track based on dimensions: ${settings.width}x${settings.height}`);
              return track;
            }
          }
        }
      }
      
      // Special case: if in screen sharing mode, and the stream is new/different
      if (isScreenSharing) {
        // Assume the current video track is a screen track if we're in screen sharing mode
        // unless it explicitly looks like a camera
        for (const track of videoTracks) {
          const label = track.label.toLowerCase();
          if (!label.includes('camera') && 
              !label.includes('webcam') && 
              !label.includes('facetime') &&
              !label.includes('iphone') &&
              !label.includes('built-in')) {
            console.log(`[useScreenShareTracker] Using track as screen track based on app state: ${label}`);
            return track;
          }
        }
        
        // If we got here while isScreenSharing is true, we have a camera-like track in a screen sharing mode
        console.log(`[useScreenShareTracker] No suitable screen tracks found despite being in screen sharing mode. Tracks:`, 
          videoTracks.map(t => t.label));
      }
      
      return null;
    };
    
    // Handle when screen sharing is active according to application state
    if (isScreenSharing && videoStream) {
      const screenTrack = findScreenTrack(videoStream);
      
      if (screenTrack) {
        // Screen track found, set up monitoring
        console.log('[useScreenShareTracker] Found screen sharing track:', screenTrack.label);
        screenTrackRef.current = screenTrack;
        
        // Add onended listener to detect when user stops sharing via browser UI
        const onTrackEnded = () => {
          console.log('[useScreenShareTracker] Screen sharing track ended via browser UI');
          // Call the callback to update app state
          stopScreenSharingCallback();
          // Clear our reference
          screenTrackRef.current = null;
        };
        
        // Add the event listener
        screenTrack.addEventListener('ended', onTrackEnded);
        
        // Clean up function
        return () => {
          screenTrack.removeEventListener('ended', onTrackEnded);
          console.log('[useScreenShareTracker] Removed track ended listener');
        };
      } else {
        // We think we're screen sharing but can't find a screen track
        console.warn('[useScreenShareTracker] Screen sharing enabled but no screen track found in stream');
        
        // Only fix state mismatch if this is a persistent issue
        // Use a timeout to prevent triggering during initial state changes
        const timeoutId = setTimeout(() => {
          if (isScreenSharing && !screenTrackRef.current) {
            console.warn('[useScreenShareTracker] State mismatch - fixing by stopping screen sharing in app state');
            correctionInProgressRef.current = true;
            stopScreenSharingCallback();
            
            // Reset correction flag after a delay
            setTimeout(() => {
              correctionInProgressRef.current = false;
            }, 500);
          }
        }, 500);
        
        return () => clearTimeout(timeoutId);
      }
    } else if (!isScreenSharing) {
      // If we're not screen sharing according to our state, clear references
      if (screenTrackRef.current) {
        console.log('[useScreenShareTracker] Screen sharing disabled, clearing track reference');
        screenTrackRef.current = null;
      }
    }
  }, [isScreenSharing, videoStream, stopScreenSharingCallback]);
  
  // Return nothing as this hook only handles side effects
};

export default useScreenShareTracker; 